import { useEffect, useState } from 'react';
import {
  FaServer,
  FaTrash,
  FaPauseCircle,
  FaPaperPlane,
  FaCalendarAlt,
  FaEdit,
} from 'react-icons/fa';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { ShimmerBroadcastCard } from '../Shimmers/ShimmerBroadcastCard';

export default function BroadcastCard({
  broadcastName,
  smtpProviderName,
  categoryLabel,
  currentDay,
  totalDays,
  status,
  imagePath = '/img/round-broadcast.png',
  scheduledDate,
  isLoading = false,
  onEdit,
  onDelete,
  onPause,
  onResume,
  onScheduleChange,
  onDuplicate,
  onDetail,
  messageName,
}) {
  const isCompleted = status === 'completed';
  const isDeleted = status === 'deleted';
  const isNew = status === 'new';
  const isActive = status === 'active';
  const isPaused = status === 'paused';

  const [editSchedule, setEditSchedule] = useState(false);
  const [inputDate, setInputDate] = useState(scheduledDate || '');

  useEffect(() => {
    setEditSchedule(false);
    setInputDate(scheduledDate || '');
  }, [scheduledDate]);

  const bgLeft = isDeleted
    ? 'bg-gray-300'
    : isCompleted
      ? 'bg-gray-300'
      : isPaused
        ? 'bg-[#cd504e]'
        : isNew
          ? 'bg-[#dfe9f5]'
          : 'bg-[#86bca2]';

  const bgRight = isDeleted ? 'bg-gray-100' : 'bg-white';

  const textColor = isDeleted
    ? 'text-gray-400'
    : isCompleted
      ? 'text-gray-400'
      : isPaused
        ? 'text-gray-700'
        : isNew
          ? 'text-gray-800'
          : 'text-gray-900';

  const rotationClass = isActive ? 'rotating-image' : '';
  const opacityClass = isDeleted ? 'opacity-50 pointer-events-none' : '';

  let leftBadge;
  if (isActive) {
    leftBadge = (
      <span
        style={{ background: '#20030340', border: '#47806657' }}
        className="flex items-center gap-2 px-3 py-1 rounded-full text-white font-semibold text-xs mt-2 shadow-sm"
        title="Mails en cours d'envoi"
      >
        <FaPaperPlane className="text-white" />
        <span className="font-normal">Envoi en cours</span>
      </span>
    );
  } else if (isPaused) {
    leftBadge = (
      <span
        style={{ background: '#812625', border: '#812625' }}
        className="flex items-center gap-2 px-2 py-1 rounded-full text-xs font-semibold mt-2 border text-white "
      >
        <FaPauseCircle className="text-white" />
        En pause
      </span>
    );
  } else if (isCompleted) {
    leftBadge = (
      <span className="flex items-center gap-2 px-2 py-1 rounded-full text-xs font-semibold mt-2 border bg-gray-200 text-gray-600 border-gray-300">
        Terminé
      </span>
    );
  } else if (isDeleted) {
    leftBadge = (
      <span className="flex items-center gap-2 px-2 py-1 rounded-full text-xs font-semibold mt-2 border bg-gray-300 text-gray-600 border-gray-300">
        Supprimé
      </span>
    );
  } else if (isNew) {
    leftBadge = (
      <span
        style={{ background: '#c1cddb', border: '#c1cddb' }}
        className="flex items-center gap-2 px-2 py-1 rounded-full text-xs font-semibold mt-2"
      >
        Nouveau
      </span>
    );
  }

  let actionIcon = null;
  if (isActive && onPause) {
    actionIcon = (
      <button
        title="Mettre en pause"
        onClick={e => {
          e.stopPropagation();
          onPause();
        }}
        className="mt-2 flex items-center text-emerald-900 opacity-80 hover:opacity-100 transition text-xs"
      >
        <FaPauseCircle className="mr-1" />
        Pause
      </button>
    );
  } else if (isPaused && onResume) {
    actionIcon = (
      <button
        title="Reprendre l'envoi"
        onClick={e => {
          e.stopPropagation();
          onResume();
        }}
        style={{ background: '#812625', border: '#812625' }}
        className="mt-2 flex items-center text-white bg-[#812625] rounded px-2 py-1 opacity-80 hover:opacity-100 transition text-xs"
      >
        <FaPaperPlane className="mr-1" />
        Reprendre
      </button>
    );
  } else if (isNew && onDelete) {
    actionIcon = (
      <button
        title="Supprimer le broadcast"
        onClick={e => {
          e.stopPropagation();
          onDelete();
        }}
        className="mt-2 flex items-center text-red-700 opacity-80 hover:opacity-100 transition text-xs"
      >
        <FaTrash className="mr-1" />
        Supprimer
      </button>
    );
  }

  let scheduleBlock = null;
  const scheduledDateParis = scheduledDate
    ? DateTime.fromISO(scheduledDate, { zone: 'Europe/Paris' })
    : null;

  if (isNew && !isDeleted) {
    const minScheduleDate = DateTime.now()
      .setZone('Europe/Paris')
      .plus({ days: 1 })
      .toISODate();
    if (editSchedule || !scheduledDate) {
      scheduleBlock = (
        <form
          className="flex flex-wrap items-center gap-2 px-6 pb-2"
          style={{ minHeight: 40 }}
          onClick={e => e.stopPropagation()}
          onSubmit={async e => {
            e.preventDefault();
            if (inputDate) {
              await onScheduleChange(inputDate);
              setEditSchedule(false);
            }
          }}
        >
          <FaCalendarAlt className="text-gray-500" />
          <input
            type="date"
            value={inputDate}
            onChange={e => setInputDate(e.target.value)}
            min={minScheduleDate}
            className="border rounded px-2 py-1 text-sm"
            style={{ minWidth: 120 }}
          />
          <button
            type="submit"
            className="bg-emerald-600 text-white px-2 py-1 rounded text-xs"
            disabled={!inputDate}
          >
            Enregistrer
          </button>
          {scheduledDate && (
            <button
              type="button"
              className="ml-1 px-2 py-1 rounded text-xs border"
              onClick={async () => {
                setInputDate('');
                await onScheduleChange(null);
                setEditSchedule(false);
              }}
            >
              Supprimer
            </button>
          )}

          <button
            type="button"
            className="ml-1 text-xs text-gray-500"
            onClick={() => setEditSchedule(false)}
          >
            Annuler
          </button>
        </form>
      );
    } else if (scheduledDateParis) {
      const nowParis = DateTime.now().setZone('Europe/Paris').startOf('day');
      const startParis = scheduledDateParis.startOf('day');
      const daysDiff = Math.max(
        0,
        Math.round(startParis.diff(nowParis, 'days').days)
      );
      let resteText = '';
      if (daysDiff === 0) {
        resteText = "Démarre aujourd'hui";
      } else if (daysDiff === 1) {
        resteText = 'Démarre demain';
      } else {
        resteText = `Démarre dans ${daysDiff} jours`;
      }
      scheduleBlock = (
        <div
          className="flex flex-wrap items-center gap-2 px-6 pb-2 text-xs text-gray-700"
          style={{ minHeight: 40 }}
        >
          <FaCalendarAlt className="text-gray-500" />
          <span>
            Programmé pour&nbsp;
            <b>{startParis.toLocaleString(DateTime.DATE_FULL)}</b>
            &nbsp;· <span className="font-semibold">{resteText}</span>
          </span>
          <button
            className="ml-1 px-2 py-1 rounded border text-xs bg-gray-100 hover:bg-gray-200"
            onClick={e => {
              e.stopPropagation();
              setEditSchedule(true);
              setInputDate(scheduledDateParis.toISODate() || '');
            }}
          >
            Modifier
          </button>
        </div>
      );
    }
  }

  if (isActive && scheduledDateParis) {
    const startParis = scheduledDateParis.startOf('day');
    const endParis = startParis.plus({ days: totalDays - 1 });
    const nowParis = DateTime.now().setZone('Europe/Paris').startOf('day');
    const daysLeft = Math.max(
      0,
      Math.round(endParis.diff(nowParis, 'days').days)
    );
    scheduleBlock = (
      <div
        className="flex flex-wrap items-center gap-2 px-6 pb-2 text-xs text-gray-700"
        style={{ minHeight: 40 }}
      >
        <FaCalendarAlt className="text-gray-500" />
        <span>
          Démarré le <b>{startParis.toLocaleString(DateTime.DATE_FULL)}</b>
          &nbsp;· Fin prévue le{' '}
          <b>{endParis.toLocaleString(DateTime.DATE_FULL)}</b>
          &nbsp;·{' '}
          <span className="font-semibold">
            {daysLeft === 0 ? 'Dernier jour' : `Il reste ${daysLeft} jours`}
          </span>
        </span>
      </div>
    );
  }

  if (isCompleted && scheduledDateParis) {
    const startParis = scheduledDateParis.startOf('day');
    const endParis = startParis.plus({ days: totalDays - 1 });
    scheduleBlock = (
      <div
        className="flex flex-wrap items-center gap-2 px-6 pb-2 text-xs text-gray-700"
        style={{ minHeight: 40 }}
      >
        <FaCalendarAlt className="text-gray-500" />
        <span>
          Du <b>{startParis.toLocaleString(DateTime.DATE_FULL)}</b>
          &nbsp;au <b>{endParis.toLocaleString(DateTime.DATE_FULL)}</b>
          &nbsp;·{' '}
          <span className="font-semibold">{totalDays} jours au total</span>
        </span>
      </div>
    );
  }

  if (isLoading) return <ShimmerBroadcastCard count={1} />;

  return (
    <div className="relative">
      {(onEdit || onDuplicate || onDetail) && (
        <div className="absolute top-2 right-2 flex items-center space-x-1 z-10">
          {onDetail && (
            <button
              title="Voir le détail"
              onClick={e => {
                e.stopPropagation();
                onDetail();
              }}
              className="text-gray-400 hover:text-orange-600 p-2 rounded transition"
            >
              👁
            </button>
          )}
          {onEdit && !isActive && (
            <button
              title="Modifier le broadcast"
              onClick={e => {
                e.stopPropagation();
                onEdit();
              }}
              className="text-gray-400 hover:text-blue-600 p-2 rounded transition"
            >
              <FaEdit size={18} />
            </button>
          )}

          {onDuplicate && (
            <button
              title="Dupliquer ce broadcast avec nouveau warmup"
              onClick={e => {
                e.stopPropagation();
                onDuplicate();
              }}
              className="text-gray-400 hover:text-green-600 p-2 rounded transition"
            >
              📋
            </button>
          )}
        </div>
      )}

      <div
        className={clsx(
          'flex rounded-xl shadow-md overflow-hidden transition items-stretch h-40',
          bgRight,
          opacityClass
        )}
        style={{ minHeight: 110, position: 'relative' }}
      >
        <div
          className={clsx(
            'flex flex-col items-center justify-center w-1/2 h-full p-2 relative',
            bgLeft
          )}
        >
          <span className="absolute top-2 -left-3 z-100 flex items-center gap-1 bg-gray-50 rounded px-2 py-1 text-xs font-medium border border-gray-200 shadow">
            <FaServer className="text-purple-700" />
            <span className="max-w-[6rem] truncate">{smtpProviderName}</span>
          </span>
          <div
            className={clsx('w-16 h-16 rounded-full', rotationClass)}
            style={{
              transition: 'filter 0.3s',
              filter:
                isCompleted || isDeleted || isPaused || isNew
                  ? 'grayscale(1) opacity(0.7)'
                  : undefined,
            }}
          >
            <img
              src={imagePath}
              alt="Broadcast"
              className="w-full h-full object-contain rounded-full"
              draggable="false"
            />
          </div>
          {leftBadge}
          {actionIcon}
        </div>

        <div className="flex-1 w-1/2 flex flex-col justify-between p-4 bg-white relative">
          <span className={`block text-lg font-bold ${textColor} mb-1`}>
            {broadcastName}
          </span>

          {messageName && (
            <span className="flex items-center gap-1 text-xs text-orange-600 font-semibold mb-1 truncate">
              <span className="text-gray-400 font-normal">Message :</span>
              {messageName}
            </span>
          )}

          {categoryLabel && (
            <span className="inline-block mb-2 text-xs bg-blue-100 text-blue-900 px-2 py-1 rounded font-semibold">
              {categoryLabel}
            </span>
          )}
          <div className="flex items-center gap-4">
            {isActive && (
              <span
                className={clsx(
                  'text-base font-semibold',
                  isCompleted
                    ? 'text-gray-600'
                    : isDeleted
                      ? 'text-gray-400'
                      : isPaused
                        ? 'text-emerald-800'
                        : isNew
                          ? 'text-emerald-800'
                          : 'text-gray-700'
                )}
              >
                {currentDay}/{totalDays} jours
              </span>
            )}
          </div>
        </div>
      </div>

      {scheduleBlock && (
        <div
          className="w-full -mt-1 pt-4 bg-white border-x border-b border-gray-200 rounded-b-xl shadow-sm"
          style={{ minHeight: 40 }}
        >
          {scheduleBlock}
        </div>
      )}
    </div>
  );
}
